import { RootState } from "@/redux/store";
import {
  useCopyFinancialTemplateMutation,
  useGetFinancialTemplateStructure,
  useGetGlMasterDataDropdown,
  usePublishFinancialTemplateMutation,
  useSaveFinancialTemplateStructureMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { EMPTY_DRAFT, errorMessage, NAME_REQUIRED, STATUS_PUBLISHED } from "../consts";
import { Cat, ConfirmState, Draft, GlUsage, Sub } from "../types";

let seq = 0;
const newKey = () => `k${Date.now()}-${seq++}`;

const move = <T,>(list: T[], from: number, to: number) => {
  if (to < 0 || to >= list.length || from === to) return list;
  const copy = [...list];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

const toPayload = (d: Draft) => ({
  title: d.title.trim(),
  description: d.description.trim() || null,
  categories: d.cats.map((c) => ({
    name: c.name.trim(),
    subcategories: c.subs.map((s) => ({ name: s.name.trim(), glMasterIds: s.gls })),
  })),
});

const useDrag = () => {
  const dragging = useRef<{ scope: string; index: number } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const bind = (scope: string, index: number, onMove: (from: number, to: number) => void, enabled: boolean) =>
    enabled
      ? {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            e.stopPropagation();
            dragging.current = { scope, index };
            e.dataTransfer.effectAllowed = "move";
          },
          onDragOver: (e: DragEvent) => {
            if (dragging.current?.scope !== scope) return;
            e.preventDefault();
            e.stopPropagation();
            setOver(`${scope}:${index}`);
          },
          onDragLeave: () => setOver(null),
          onDrop: (e: DragEvent) => {
            if (dragging.current?.scope !== scope) return;
            e.preventDefault();
            e.stopPropagation();
            onMove(dragging.current.index, index);
            dragging.current = null;
            setOver(null);
          },
          onDragEnd: () => {
            dragging.current = null;
            setOver(null);
          },
        }
      : {};
  const isOver = (scope: string, index: number) => over === `${scope}:${index}`;
  return { bind, isOver };
};

const useFinancialVersionBuilder = () => {
  const { financialTemplateId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const community = useSelector((state: RootState) => state.currentData.communityData);
  const { data: structure, isLoading } = useGetFinancialTemplateStructure({ financialTemplateId });
  const template = structure?.template;
  const readOnly = template?.status === STATUS_PUBLISHED;
  const { data: glData } = useGetGlMasterDataDropdown({
    communityId: template?.communityId ? String(template.communityId) : "",
  });

  const [draft, setDraft] = useState<Draft>({ ...EMPTY_DRAFT, cats: [] });
  const [saved, setSaved] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<string[]>([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { bind, isOver } = useDrag();

  useEffect(() => {
    if (!structure) return;
    const d: Draft = {
      title: structure.template?.title || "",
      description: structure.template?.description || "",
      cats: (structure.categories || []).map((c: any) => ({
        key: newKey(), name: c.name,
        subs: c.subcategories.map((s: any) => ({ key: newKey(), name: s.name, gls: s.gls.map((g: any) => g.glMasterId) })),
      })),
    };
    setDraft(d);
    setSaved(JSON.stringify(toPayload(d)));
    setErrors([]);
  }, [structure]);

  const dirty = !readOnly && !!saved && JSON.stringify(toPayload(draft)) !== saved;
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const glOptions: any[] = useMemo(() => (glData?.data || []).filter((g: any) => g.active !== false), [glData]);
  const glById = useMemo(() => {
    const m = new Map<number, any>();
    (glData?.data || []).forEach((g: any) => m.set(g.glMasterId, g));
    (structure?.categories || []).forEach((c: any) =>
      c.subcategories.forEach((s: any) => s.gls.forEach((g: any) => !m.has(g.glMasterId) && m.set(g.glMasterId, g)))
    );
    return m;
  }, [glData, structure]);
  const usage = useMemo(() => {
    const m = new Map<number, GlUsage>();
    draft.cats.forEach((c) =>
      c.subs.forEach((s) => s.gls.forEach((id) => m.set(id, { subKey: s.key, where: `${c.name || "Category"} › ${s.name || "Subcategory"}` })))
    );
    return m;
  }, [draft]);
  const unassigned = glOptions.filter((g) => !usage.has(g.glMasterId));
  const subCount = draft.cats.reduce((n, c) => n + c.subs.length, 0);

  const update = (fn: (d: Draft) => Draft) => setDraft((d) => fn(d));
  const setCats = (fn: (cats: Cat[]) => Cat[]) => update((d) => ({ ...d, cats: fn(d.cats) }));
  const setCat = (key: string, fn: (c: Cat) => Cat) => setCats((cats) => cats.map((c) => (c.key === key ? fn(c) : c)));
  const setSub = (catKey: string, subKey: string, fn: (s: Sub) => Sub) =>
    setCat(catKey, (c) => ({ ...c, subs: c.subs.map((s) => (s.key === subKey ? fn(s) : s)) }));
  const addCategory = () => {
    const cat = { key: newKey(), name: "", subs: [{ key: newKey(), name: "", gls: [] }] };
    setCats((cats) => [...cats, cat]);
  };
  const addSub = (catKey: string) => setCat(catKey, (c) => ({ ...c, subs: [...c.subs, { key: newKey(), name: "", gls: [] }] }));
  const toggle = (key: string) => setCollapsed((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const allCollapsed = draft.cats.length > 0 && draft.cats.every((c) => collapsed.has(c.key));
  const toggleAllCollapsed = () => setCollapsed(allCollapsed ? new Set() : new Set(draft.cats.map((c) => c.key)));

  const validate = (forPublish: boolean) => {
    const errs: string[] = [];
    if (!draft.title.trim()) errs.push(NAME_REQUIRED);
    const catNames = new Set<string>();
    draft.cats.forEach((c, ci) => {
      const cn = c.name.trim();
      if (!cn) errs.push(`Category ${ci + 1} needs a name.`);
      else if (catNames.has(cn.toLowerCase())) errs.push(`Category "${cn}" is used more than once.`);
      catNames.add(cn.toLowerCase());
      const subNames = new Set<string>();
      if (forPublish && c.subs.length === 0) errs.push(`Category "${cn || ci + 1}" has no subcategories.`);
      c.subs.forEach((s, si) => {
        const sn = s.name.trim();
        if (!sn) errs.push(`Subcategory ${si + 1} in "${cn || `Category ${ci + 1}`}" needs a name.`);
        else if (subNames.has(sn.toLowerCase())) errs.push(`Subcategory "${sn}" is used more than once in "${cn}".`);
        subNames.add(sn.toLowerCase());
        if (forPublish && s.gls.length === 0) errs.push(`Subcategory "${sn || si + 1}" in "${cn}" has no GL accounts.`);
      });
    });
    if (forPublish && draft.cats.length === 0) errs.push("Add at least one category before publishing.");
    setErrors(errs);
    return errs.length === 0;
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["financial-template-structure", financialTemplateId] });
    queryClient.invalidateQueries({ queryKey: ["financial-templates"] });
  };
  const { mutateAsync: saveAsync, isPending: isSaving } = useSaveFinancialTemplateStructureMutation();
  const { mutateAsync: publishAsync, isPending: isPublishing } = usePublishFinancialTemplateMutation();
  const { mutate: copy, isPending: isCopying } = useCopyFinancialTemplateMutation({
    onError: (e) => toast.error(errorMessage(e)),
    onSuccess: (res: any) => {
      const v = res?.data?.data;
      toast.success(`Version ${v?.versionNo} created as a draft`);
      queryClient.invalidateQueries({ queryKey: ["financial-templates"] });
      navigate(`../${v?.financialTemplateId}`, { relative: "path" });
    },
  });

  const save = async () => {
    if (!validate(false)) return false;
    try {
      await saveAsync({ financialTemplateId: financialTemplateId!, data: toPayload(draft) });
      setSaved(JSON.stringify(toPayload(draft)));
      refresh();
      return true;
    } catch (e) {
      setErrors([errorMessage(e)]);
      return false;
    }
  };
  const onSaveDraft = async () => {
    if (await save()) toast.success("Draft saved");
  };
  const onPublish = () => {
    if (!validate(true)) return;
    setConfirm({
      title: "Publish this version?",
      text: `Version ${template?.versionNo} becomes read-only and can be used in reports. Later changes go into a new version.${unassigned.length ? ` ${unassigned.length} active GL account(s) are not assigned.` : ""}`,
      action: async () => {
        if (dirty && !(await save())) return;
        try {
          await publishAsync({ financialTemplateId: financialTemplateId! });
          toast.success("Version published");
          refresh();
        } catch (e) {
          setErrors([errorMessage(e)]);
        }
      },
    });
  };
  const back = () => navigate("..", { relative: "path" });
  const onCancel = () => (dirty
    ? setConfirm({ title: "Discard changes?", text: "Your unsaved changes will be lost.", danger: true, action: back })
    : back());

  const moveCat = (from: number, to: number) => setCats((cats) => move(cats, from, to));
  const moveSub = (catKey: string, from: number, to: number) =>
    setCat(catKey, (c) => ({ ...c, subs: move(c.subs, from, to) }));
  const moveGl = (catKey: string, subKey: string, from: number, to: number) =>
    setSub(catKey, subKey, (s) => ({ ...s, gls: move(s.gls, from, to) }));
  const addGl = (catKey: string, subKey: string, id: number) =>
    setSub(catKey, subKey, (s) => ({ ...s, gls: [...s.gls, id] }));
  const removeGl = (catKey: string, subKey: string, id: number) =>
    setSub(catKey, subKey, (s) => ({ ...s, gls: s.gls.filter((x) => x !== id) }));
  const removeSub = (catKey: string, subKey: string) =>
    setCat(catKey, (c) => ({ ...c, subs: c.subs.filter((s) => s.key !== subKey) }));
  const removeCat = (catKey: string) => setCats((cats) => cats.filter((c) => c.key !== catKey));
  const onDeleteCategory = (cat: Cat, ci: number) => {
    if (cat.subs.some((s) => s.name || s.gls.length) || cat.name) {
      setConfirm({
        title: "Delete category?",
        text: `"${cat.name || `Category ${ci + 1}`}" and its ${cat.subs.length} subcategories will be removed when you save.`,
        danger: true,
        action: () => removeCat(cat.key),
      });
    } else {
      removeCat(cat.key);
    }
  };
  const closeConfirm = () => setConfirm(null);
  const runConfirm = () => {
    const a = confirm?.action;
    setConfirm(null);
    a?.();
  };
  const onCopyVersion = () => copy({ financialTemplateId: financialTemplateId! });

  return {
    financialTemplateId,
    community,
    structure,
    isLoading,
    template,
    readOnly,
    draft,
    update,
    setCat,
    setSub,
    addCategory,
    addSub,
    collapsed,
    toggle,
    allCollapsed,
    toggleAllCollapsed,
    errors,
    showUnassigned,
    setShowUnassigned,
    confirm,
    closeConfirm,
    runConfirm,
    bind,
    isOver,
    dirty,
    busy: isSaving || isPublishing,
    isCopying,
    glOptions,
    glById,
    usage,
    unassigned,
    subCount,
    onSaveDraft,
    onPublish,
    onCancel,
    onCopyVersion,
    moveCat,
    moveSub,
    moveGl,
    addGl,
    removeGl,
    removeSub,
    onDeleteCategory,
  };
};

export default useFinancialVersionBuilder;
